/**
 * Versioned marketing SMS consent language registry.
 *
 * All surfaces that display marketing SMS consent copy should import
 * MARKETING_SMS_CONSENT from here.
 */
import React from 'react';

import styled from '@emotion/styled';
import { Box, type BoxProps, Link, Text, type TextProps } from '@opendoor/bricks-next';

// ---------------------------------------------------------------------------
// Consent language registry
// ---------------------------------------------------------------------------

interface CtaAgreementLinkedProps {
  ctaText: string;
  tosAnalyticsName: string;
  privacyAnalyticsName: string;
  tosUrl?: string;
  privacyUrl?: string;
  linkProps?: Record<string, unknown>;
}

interface ConsentLanguageEntry {
  checkboxLabel: string;
  ctaAgreement: (ctaText: string) => string;
  full: (ctaText: string) => string;
  CtaAgreementLinked: React.FC<CtaAgreementLinkedProps>;
}

/** Checkbox rendered on registration; only drivesPreference entries update marketing opt-in. */
export type SmsConsentCheckboxSpec = {
  label: string;
  name: string;
  analyticsSuffix: string;
  drivesPreference?: boolean;
};

const CtaAgreementLinked: React.FC<CtaAgreementLinkedProps> = ({
  ctaText,
  tosAnalyticsName,
  privacyAnalyticsName,
  tosUrl = 'https://www.opendoor.com/w/terms',
  privacyUrl = 'https://www.opendoor.com/w/privacy',
  linkProps = {},
}) => (
  <>
    By pressing &ldquo;{ctaText},&rdquo; I agree to Opendoor&rsquo;s{' '}
    <Link
      analyticsName={tosAnalyticsName}
      href={tosUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Opens a new page with Opendoor’s terms of service"
      onPress={() => {}}
      {...linkProps}
    >
      Terms of Service
    </Link>{' '}
    and{' '}
    <Link
      analyticsName={privacyAnalyticsName}
      href={privacyUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Opens a new page with Opendoor’s privacy policy"
      onPress={() => {}}
      {...linkProps}
    >
      Privacy Policy
    </Link>
    .
  </>
);

// DO NOT MODIFY EXISTING ENTRIES. IF YOU NEED TO UPDATE THE CONSENT LANGUAGE, ADD A NEW ENTRY TO THE OBJECT.
// WHEN LEGAL UPDATES THE COPY:
//   1. Add a new entry to CONSENT_VERSIONS (do NOT modify existing entries)
//   2. Update CURRENT_VERSION to point to the new key
//   3. Update the test
const CONSENT_VERSIONS: Record<string, ConsentLanguageEntry> = {
  marketing_sms_v1: {
    checkboxLabel:
      'I consent to receive marketing calls and texts from Opendoor and its affiliates to the number provided. I understand that I may opt out at any time and consent is not a condition of purchase.',
    ctaAgreement: (ctaText: string) =>
      `By pressing \u201c${ctaText},\u201d I agree to Opendoor\u2019s Terms of Service and Privacy Policy.`,
    full: (ctaText: string) =>
      `I consent to receive marketing calls and texts from Opendoor and its affiliates to the number provided. I understand that I may opt out at any time and consent is not a condition of purchase. By pressing \u201c${ctaText},\u201d I agree to Opendoor\u2019s Terms of Service and Privacy Policy.`,
    CtaAgreementLinked,
  },
  // Account/offer SMS consent used on the cosmos (opendoor.com/login) signup
  // form. Checking the box opts in to both marketing and transactional SMS.
  marketing_sms_v2: {
    checkboxLabel:
      'By providing your phone number, you agree to receive account and offer-related text messages from Opendoor at the number provided. Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to cancel. Consent is not a condition of purchase.',
    ctaAgreement: (ctaText: string) =>
      `By pressing “${ctaText},” I agree to Opendoor’s Terms of Service and Privacy Policy.`,
    full: (ctaText: string) =>
      `By providing your phone number, you agree to receive account and offer-related text messages from Opendoor at the number provided. Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to cancel. Consent is not a condition of purchase. By pressing “${ctaText},” I agree to Opendoor’s Terms of Service and Privacy Policy.`,
    CtaAgreementLinked,
  },
};

type ConsentVersion = keyof typeof CONSENT_VERSIONS;

const CURRENT_VERSION: ConsentVersion = 'marketing_sms_v1';

export const MARKETING_SMS_CONSENT = {
  version: CURRENT_VERSION,
  ...CONSENT_VERSIONS[CURRENT_VERSION],
};

// Account/offer SMS consent (v2) for the cosmos login signup form. Exposed
// separately from MARKETING_SMS_CONSENT (which stays on the current marketing
// copy) so the login surface can render and log this specific version.
export const MARKETING_SMS_CONSENT_V2 = {
  version: 'marketing_sms_v2' as ConsentVersion,
  ...CONSENT_VERSIONS['marketing_sms_v2'],
};

const MARKETING_SMS_V3_ACCOUNT_LABEL =
  'By providing your phone number and checking this box, I agree to receive account-related text messages from Opendoor at the number provided, including offer status updates, transaction alerts, and support notices. Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to cancel. Consent is not a condition of purchase.';
const MARKETING_SMS_V3_OFFER_LABEL =
  'By providing your phone number and checking this box, I agree to receive offer-related and promotional text messages from Opendoor at the number provided, including cash offer reminders, promotions, and selling updates. Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to cancel. Consent is not a condition of purchase.';

/**
 * Dual account vs offer SMS consent for cosmos /login (Twilio RCS).
 * Offer drives marketingSmsOptOut today; account is display-only until wired.
 */
export const MARKETING_SMS_CONSENT_V3 = {
  version: 'marketing_sms_v3',
  checkboxes: [
    {
      label: MARKETING_SMS_V3_ACCOUNT_LABEL,
      name: 'account-sms-opt-in',
      analyticsSuffix: 'account-sms-checkbox',
    },
    {
      label: MARKETING_SMS_V3_OFFER_LABEL,
      name: 'offer-sms-opt-in',
      analyticsSuffix: 'offer-sms-checkbox',
      drivesPreference: true,
    },
  ] satisfies ReadonlyArray<SmsConsentCheckboxSpec>,
  ctaAgreement: (ctaText: string) =>
    `By pressing “${ctaText},” I agree to Opendoor’s Terms of Service and Privacy Policy.`,
  full: (ctaText: string) =>
    `${MARKETING_SMS_V3_ACCOUNT_LABEL} ${MARKETING_SMS_V3_OFFER_LABEL} By pressing “${ctaText},” I agree to Opendoor’s Terms of Service and Privacy Policy.`,
  CtaAgreementLinked,
};

// ---------------------------------------------------------------------------
// Shared auth UI components
// ---------------------------------------------------------------------------

export const EmailDisclaimerText: React.FC<{ analyticsName: string }> = ({ analyticsName }) => {
  return (
    <Text
      tag="p"
      typography="$bodyXsmall"
      whiteSpace="normal"
      $largerThanSM={{ whiteSpace: 'nowrap' }}
    >
      <MARKETING_SMS_CONSENT.CtaAgreementLinked
        ctaText="Continue"
        tosAnalyticsName={`${analyticsName}-terms`}
        privacyAnalyticsName={`${analyticsName}-privacy-policy`}
        linkProps={{ variant: 'primary', size: 'xsmall' }}
      />
    </Text>
  );
};

export const AccountFormHeader: React.FC<Omit<TextProps, 'typography' | 'tag'>> = ({
  children,
  ...rest
}) => {
  return (
    <Text tag="h1" typography="$headerMedium" {...rest} marginBottom="$10x" textAlign="left">
      {children}
    </Text>
  );
};

export const AccountFormWrapper: React.FC<BoxProps> = (props) => {
  return <Box width="100%" {...props} />;
};

export const FormWrapper: React.FC<BoxProps> = (props) => {
  return <Box width="100%" flexDirection="column" {...props} />;
};

export type DisclaimerProps = {
  location?: 'top' | 'bottom';
  formatText?: (formatProps: { actionText: string }) => string;
};

// eslint-disable-next-line no-restricted-syntax
export const Form = styled.form`
  width: 100%;
  margin-top: 28px; /* $7x token equivalent */
`;

export const Disclaimer: React.FC<
  BoxProps & { desktopNoWrap?: boolean; shouldRenderDisclaimerAboveCTA?: boolean }
> = ({ children, desktopNoWrap = false, shouldRenderDisclaimerAboveCTA, ...props }) => {
  return (
    <Box {...props}>
      <Text
        tag="p"
        typography="$bodyXsmall"
        color="$contentSecondary"
        whiteSpace="normal"
        $largerThanSM={desktopNoWrap ? { whiteSpace: 'nowrap' } : undefined}
      >
        {children}
      </Text>
    </Box>
  );
};
